import { z } from 'zod';
import { uuidSchema } from './common.js';
/**
 * Wire schemas for `GET /admin/users` — the staff-picker surface
 * (Wave 2.56 BE-2, TASK-236-followup-be).
 *
 * Distinct from `admins.ts` (which owns the `/admin/settings/admins/*`
 * management surface): the picker is the thin "who can be assigned as
 * `manager_user_id` on a customer account" lookup feeding form selects
 * (account create page, future account team / assignee dropdowns). It
 * deliberately omits `is_super_admin`, `last_login_at`, `created_at`,
 * `is_active` (the endpoint already filters to active users only) so the
 * payload stays small for combobox use.
 *
 * Role filter (default + only currently allowed value): `admin`. The query
 * accepts a CSV `role` parameter that whitelists `admin`/`super_admin` for
 * forward-compat — selectors and tenant users never appear in the picker.
 *
 * Pagination: keyset cursor on `(name, id)` ascending. The FE picker is
 * a small combobox — 100 rows per page is plenty; tariffs follow the same
 * shape and are usually fetched in one page.
 */
export const ADMIN_USER_PICKER_ROLES = ['admin', 'super_admin'];
export const adminUserPickerRoleSchema = z.enum(ADMIN_USER_PICKER_ROLES);
const Q_MAX = 200;
const CURSOR_MAX = 200;
const ROLE_IN_MAX = ADMIN_USER_PICKER_ROLES.length;
const csvRoleArraySchema = z.preprocess((raw) => {
    if (raw === undefined || raw === null || raw === '')
        return undefined;
    if (Array.isArray(raw))
        return raw;
    if (typeof raw === 'string') {
        return raw
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s.length > 0);
    }
    return raw;
}, z.array(adminUserPickerRoleSchema).max(ROLE_IN_MAX).optional());
export const adminUserPickerFilterSchema = z.object({
    role: csvRoleArraySchema,
    q: z.string().trim().min(1).max(Q_MAX).optional(),
    limit: z.coerce.number().int().positive().max(100).default(50),
    cursor: z.string().max(CURSOR_MAX).optional(),
});
export const adminUserPickerItemSchema = z.object({
    id: uuidSchema,
    email: z.string(),
    name: z.string(),
    role: adminUserPickerRoleSchema,
});
export const adminUserPickerResponseSchema = z.object({
    items: z.array(adminUserPickerItemSchema),
    next_cursor: z.string().nullable(),
});
//# sourceMappingURL=admin-user.js.map